import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { 
  MoonIcon, 
  SunIcon, 
  Menu, 
  X, 
  Globe, 
  LogIn, 
  UserPlus, 
  BarChart2, 
  BookOpen,
  Users,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Search,
  ArrowRight,
} from 'lucide-react'
import { useLanguage } from '@/store/language-store'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../ThemeProvider'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { languages } from '@/components/settings/LanguageSettings'
import LogoDark from '../../assets/logo-dark.svg?url'
import LogoLight from '../../assets/logo-light.svg?url'

const navItems = [
  {
    label: 'product',
    icon: BarChart2,
    items: [
      { label: 'features', href: '#features' },
      { label: 'pricing', href: '#pricing' },
      { label: 'faq', href: '#faq' },
    ]
  },
  {
    label: 'company',
    icon: Users,
    items: [
      { label: 'aboutUs', href: '/about' },
      { label: 'contact', href: '/contact' },
      { label: 'careers', href: '/careers' },
    ]
  },
  {
    label: 'resources',
    icon: BookOpen,
    items: [
      { label: 'blog', href: '/blog' },
      { label: 'helpCenter', href: '/help' },
      { label: 'community', href: '/community' },
    ]
  }
]

export function Navbar() {
  const { theme, setTheme } = useTheme()
  const { language, setLanguage } = useLanguage()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('')
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const { t } = useTranslation()
  const isRTL = language === 'ar'
  const { user } = useAuth()

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)

  const handleNavigation = (href: string) => {
    if (href.startsWith('#')) {
      const element = document.getElementById(href.replace('#', ''))
      if (element) {
        const navHeight = 64 // height of navbar
        const y = element.getBoundingClientRect().top + window.pageYOffset - navHeight
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    } else {
      navigate(href)
    }
    setIsMenuOpen(false)
    setExpandedCategory(null)
  }

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.flatMap(item => 
        item.items.filter(subItem => subItem.href.startsWith('#'))
      )
      const scrollPosition = window.scrollY + 100 // offset for better detection

      sections.forEach((section) => {
        const element = document.getElementById(section.href.replace('#', ''))
        if (element) {
          const sectionTop = element.offsetTop
          const sectionBottom = sectionTop + element.offsetHeight

          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            setActiveSection(section.href)
          }
        }
      })
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const isDark = theme === 'dark'

  return (
    <nav className={cn(
      "fixed top-0 w-full z-[40] bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border-b border-border dark:border-border-dark shadow-sm transition-all duration-200",
      isRTL && "font-arabic"
    )}>
      <div className={cn(
        "container mx-auto px-4 h-16 flex items-center",
        isRTL && "flex-row-reverse"
      )}>
        {/* Logo */}
        <div className={cn(
          "flex items-center flex-1",
          isRTL && "flex-row-reverse"
        )}>
          <a 
            href="/" 
            className={cn(
              "hover:opacity-80 transition-opacity duration-200",
              isRTL ? "ml-8" : "mr-8"
            )}
          >
            <div className="hover:opacity-80 transition-opacity duration-200">
              <img 
                src={isDark ? LogoDark : LogoLight}
                alt="Flows.Cash Logo"
                className="h-8 w-auto"
              />
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center">
            {/* Navigation Dropdowns */}
            <div className={cn(
              "flex items-center",
              isRTL && "flex-row-reverse"
            )}>
              {navItems.map((category) => {
                const Icon = category.icon
                return (
                  <DropdownMenu key={category.label}>
                    <DropdownMenuTrigger asChild>
                      <button className={cn(
                        "h-11 px-4 rounded-lg flex items-center gap-2 transition-all duration-200 hover:bg-secondary/10 text-secondary hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark",
                        isRTL && "flex-row-reverse"
                      )}>
                        <Icon className="h-4 w-4 text-secondary dark:text-primary-dark" />
                        <span className="font-medium">{t(`navbar.${category.label}.title`)}</span>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align={isRTL ? "end" : "start"} 
                      sideOffset={8} 
                      className="w-[200px] bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark"
                    >
                      {category.items.map((item) => (
                        <DropdownMenuItem
                          key={item.href}
                          onClick={() => handleNavigation(item.href)}
                          className={cn(
                            "cursor-pointer transition-colors duration-200 text-text-primary dark:text-text-dark-primary",
                            activeSection === item.href
                              ? "bg-secondary/10 text-secondary dark:bg-primary-dark/10 dark:text-primary-dark"
                              : "hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark",
                            isRTL && "flex-row-reverse text-right"
                          )}
                        >
                          {t(`navbar.menu.${item.label}`)}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
              })}
            </div>
          </div>
        </div>

        {/* Right Side Actions */}
        <div className={cn(
          "flex items-center gap-3",
          isRTL && "flex-row-reverse"
        )}>
          {/* Desktop Actions */}
          <div className={cn(
            "hidden md:flex items-center gap-3",
            isRTL && "flex-row-reverse"
          )}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-11 w-11 hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark transition-colors duration-200"
                >
                  {languages.find(lang => lang.code === language)?.flag || <Globe className="h-5 w-5" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align={isRTL ? "start" : "end"} 
                className="w-[200px] bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark"
              >
                <DropdownMenuLabel className="text-text-primary dark:text-text-dark-primary">{t('navbar.language.select')}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-border dark:bg-border-dark" />
                {languages.map((lang) => (
                  <DropdownMenuItem 
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={cn(
                      "cursor-pointer flex items-center gap-2 transition-colors duration-200",
                      language === lang.code 
                        ? "bg-secondary/10 text-secondary dark:bg-primary-dark/10 dark:text-primary-dark" 
                        : "hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark",
                      isRTL && "flex-row-reverse text-right"
                    )}
                  >
                    {lang.flag}
                    <span>{lang.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              className="h-11 w-11 hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark transition-colors duration-200"
            >
              {isDark ? (
                <SunIcon className="h-5 w-5" />
              ) : (
                <MoonIcon className="h-5 w-5" />
              )}
            </Button>

            {user ? (
              <Button
                className={cn(
                  "h-11 px-6 bg-primary text-white hover:bg-primary/90 shadow-sm rounded-xl transition-all duration-200 hover:shadow-md active:scale-[0.98] dark:bg-primary-dark dark:hover:bg-primary-dark/90",
                  isRTL && "flex-row-reverse"
                )}
                onClick={() => navigate('/app/dashboard')}
              >
                <LayoutDashboard className="h-5 w-5 mr-2" />
                {t('navbar.dashboard')}
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/auth/login')}
                  className={cn(
                    "h-11 px-6 hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark transition-colors duration-200",
                    isRTL && "flex-row-reverse"
                  )}
                >
                  {t('navbar.auth.login')}
                </Button>
                <Button
                  className={cn(
                    "h-11 px-6 bg-primary text-white hover:bg-primary/90 shadow-sm rounded-xl transition-all duration-200 hover:shadow-md active:scale-[0.98] dark:bg-primary-dark dark:hover:bg-primary-dark/90",
                    isRTL && "flex-row-reverse"
                  )}
                  onClick={() => navigate('/auth/signup')}
                >
                  {t('navbar.auth.signup')}
                </Button>
              </>
            )}
          </div>

          {/* Mobile Actions */}
          <div className={cn(
            "md:hidden flex items-center gap-2",
            isRTL && "flex-row-reverse"
          )}>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              className="h-11 w-11 hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark transition-colors duration-200"
            >
              {isDark ? (
                <SunIcon className="h-5 w-5" />
              ) : (
                <MoonIcon className="h-5 w-5" />
              )}
            </Button>

            <button
              onClick={toggleMenu}
              className="h-11 w-11 rounded-xl flex items-center justify-center hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark transition-colors duration-200 active:scale-[0.98]"
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <>
          {/* Mobile Menu Backdrop */}
          <div 
            className="fixed inset-0 bg-black/60 md:hidden z-[45]"
            onClick={() => {
              setIsMenuOpen(false)
              setExpandedCategory(null)
            }}
            aria-hidden="true"
          />

          {/* Mobile Menu Content */}
          <div className="fixed top-16 left-0 right-0 w-full md:hidden z-[46] bg-background-secondary/90 dark:bg-background-dark-secondary/90 backdrop-blur-xl backdrop-saturate-150 shadow-xl">
            <div className="max-h-[calc(100vh-4rem)] flex flex-col">
              {/* Navigation Items */}
              <div className="flex-1 overflow-y-auto bg-background-secondary/90 dark:bg-background-dark-secondary/90 backdrop-blur-xl backdrop-saturate-150">
                <div className="p-4 space-y-4">
                  {navItems.map((category) => {
                    const Icon = category.icon
                    const isExpanded = expandedCategory === category.label
                    return (
                      <div key={category.label} className="border-b border-border dark:border-border-dark last:border-0 pb-2">
                        <button
                          onClick={() => setExpandedCategory(isExpanded ? null : category.label)}
                          className={cn(
                            "w-full p-3 rounded-lg flex items-center justify-between text-text-primary dark:text-text-dark-primary hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark transition-colors duration-200",
                            isRTL && "flex-row-reverse"
                          )}
                        >
                          <div className={cn(
                            "flex items-center gap-2",
                            isRTL && "flex-row-reverse"
                          )}>
                            <Icon className="h-5 w-5 text-secondary dark:text-primary-dark" />
                            <span className="font-medium">{t(`navbar.${category.label}.title`)}</span>
                          </div>
                          <ChevronRight className={cn(
                            "h-5 w-5 transition-transform duration-200",
                            isExpanded && "rotate-90",
                            isRTL && "rotate-180"
                          )} />
                        </button>
                        {isExpanded && (
                          <div className={cn(
                            "pl-4 space-y-1 mt-1",
                            isRTL && "pr-4 pl-0"
                          )}>
                            {category.items.map((item) => (
                              <button
                                key={item.href}
                                onClick={() => handleNavigation(item.href)}
                                className={cn(
                                  "w-full p-2 rounded-lg text-left transition-colors duration-200 flex items-center",
                                  activeSection === item.href
                                    ? "text-secondary bg-secondary/10 dark:text-primary-dark dark:bg-primary-dark/10"
                                    : "text-text-secondary dark:text-text-dark-secondary hover:text-secondary hover:bg-secondary/10 dark:hover:text-primary-dark dark:hover:bg-primary-dark/10",
                                  isRTL && "flex-row-reverse text-right"
                                )}
                              >
                                <ChevronRight className={cn(
                                  "h-4 w-4 opacity-50",
                                  isRTL ? "ml-2 rotate-180" : "mr-2"
                                )} />
                                {t(`navbar.menu.${item.label}`)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Mobile Menu Footer */}
              <div className="border-t border-border dark:border-border-dark bg-background-secondary/90 dark:bg-background-dark-secondary/90 backdrop-blur-xl backdrop-saturate-150">
                {/* Language Selector */}
                <div className="p-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="outline" 
                        className={cn(
                          "w-full h-11 justify-between font-medium border-secondary text-secondary hover:bg-secondary/10 dark:border-primary-dark dark:text-primary-dark dark:hover:bg-primary-dark/10",
                          isRTL && "flex-row-reverse"
                        )}
                      >
                        <div className={cn(
                          "flex items-center gap-2",
                          isRTL && "flex-row-reverse"
                        )}>
                          <Globe className="h-5 w-5" />
                          <span>{languages.find(lang => lang.code === language)?.nativeName || 'Select Language'}</span>
                        </div>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align={isRTL ? "start" : "end"} 
                      sideOffset={0}
                      className="w-[200px] bg-background-secondary/90 dark:bg-background-dark-secondary/90 backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark z-[47]"
                    >
                      <DropdownMenuLabel className="text-text-primary dark:text-text-dark-primary">{t('navbar.language.select')}</DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-border dark:bg-border-dark" />
                      {languages.map((lang) => (
                        <DropdownMenuItem 
                          key={lang.code}
                          onClick={() => setLanguage(lang.code)}
                          className={cn(
                            "cursor-pointer flex items-center gap-2 transition-colors duration-200",
                            language === lang.code 
                              ? "bg-secondary/10 text-secondary dark:bg-primary-dark/10 dark:text-primary-dark" 
                              : "hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark",
                            isRTL && "flex-row-reverse text-right"
                          )}
                        >
                          <span>{lang.flag}</span>
                          <span>{lang.nativeName}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Auth Buttons */}
                <div className="p-4 space-y-2">
                  {user ? (
                    <Button
                      className={cn(
                        "w-full h-11 bg-primary text-white hover:bg-primary/90 shadow-sm rounded-xl transition-all duration-200 hover:shadow-md active:scale-[0.98] flex items-center justify-center gap-2 font-medium",
                        isRTL && "flex-row-reverse"
                      )}
                      onClick={() => {
                        navigate('/dashboard')
                        setIsMenuOpen(false)
                      }}
                    >
                      <LayoutDashboard className="h-5 w-5" />
                      {t('navbar.dashboard')}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate('/auth/login')
                          setIsMenuOpen(false)
                        }}
                        className={cn(
                          "w-full h-11 justify-center font-medium border-secondary text-secondary hover:bg-secondary/10 transition-colors duration-200 dark:border-primary-dark dark:text-primary-dark dark:hover:bg-primary-dark/10",
                          isRTL && "flex-row-reverse"
                        )}
                      >
                        <LogIn className={cn("h-5 w-5", isRTL ? "ml-2" : "mr-2")} />
                        {t('navbar.auth.login')}
                      </Button>
                      <Button
                        onClick={() => {
                          navigate('/auth/signup')
                          setIsMenuOpen(false)
                        }}
                        className={cn(
                          "w-full h-11 bg-primary text-white hover:bg-primary/90 shadow-sm rounded-xl transition-all duration-200 hover:shadow-md active:scale-[0.98] flex items-center justify-center gap-2 font-medium dark:bg-primary-dark dark:hover:bg-primary-dark/90",
                          isRTL && "flex-row-reverse"
                        )}
                      >
                        <UserPlus className="h-5 w-5" />
                        {t('navbar.auth.signup')}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  )
}
